/**
 * Redux Pagination Helpers
 * Shared utilities for sparse array pagination pattern
 *
 * Eliminates code duplication across member, taskItem, taskBudget, and taskPerformance slices
 */

import type { PayloadAction } from '@reduxjs/toolkit';

/**
 * Generic pagination metadata from API response
 */
export interface PaginationMeta {
  page: number;
  per_page: number;
  total: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

/**
 * Generic paginated state interface
 * Use this as base for slice states that need pagination
 */
export interface PaginatedState<T> {
  items: T[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
  loadedPages: Set<number>;
}

/**
 * Placeholder item interface
 * Used to fill sparse array gaps
 */
interface PlaceholderItem {
  id: string;
  isPlaceholder: true;
}

/**
 * Creates a sparse array with placeholders for efficient server-side pagination
 *
 * @param data - First page data
 * @param meta - Pagination metadata
 * @param placeholderPrefix - Prefix for placeholder IDs (e.g., 'task-item', 'task-budget')
 * @returns Sparse array with real data in first positions and placeholders for remaining items
 *
 * @example
 * // For first page with 100 items out of 1000 total
 * const sparseArray = createSparseArray(firstPageData, meta, 'task-item')
 * // Returns: [item1, item2, ..., item100, {id: 'placeholder-task-item-100', isPlaceholder: true}, ...]
 */
export function createSparseArray<T extends { id: string }>(
  data: T[],
  meta: PaginationMeta,
  placeholderPrefix: string
): (T | PlaceholderItem)[] {
  const sparseArray: (T | PlaceholderItem)[] = [];

  for (let i = 0; i < meta.total; i++) {
    if (i < data.length) {
      // First page: real data
      sparseArray.push(data[i]);
    } else {
      // Remaining positions: placeholders
      sparseArray.push({
        id: `placeholder-${placeholderPrefix}-${i}`,
        isPlaceholder: true
      });
    }
  }

  return sparseArray;
}

/**
 * Inserts page data at correct position in sparse array
 * Ensures array has sufficient length and replaces placeholders with real data
 *
 * @param items - Current sparse array
 * @param data - New page data to insert
 * @param page - Page number (1-indexed)
 * @param pageSize - Number of items per page
 * @param totalItems - Total items from API meta
 * @param placeholderPrefix - Prefix for placeholder IDs
 * @returns Updated sparse array with new data at correct position
 *
 * @example
 * // Insert page 3 data into existing sparse array
 * const updated = insertPageData(currentItems, page3Data, 3, 100, 1000, 'task-item')
 * // Data is inserted at index 200-299
 */
export function insertPageData<T extends { id: string }>(
  items: (T | PlaceholderItem)[],
  data: T[],
  page: number,
  pageSize: number,
  totalItems: number,
  placeholderPrefix: string
): (T | PlaceholderItem)[] {
  const updatedItems = [...items];
  const startIndex = (page - 1) * pageSize;

  // Ensure array has sufficient length
  const requiredLength = Math.max(updatedItems.length, totalItems);
  while (updatedItems.length < requiredLength) {
    updatedItems.push({
      id: `placeholder-${placeholderPrefix}-${updatedItems.length}`,
      isPlaceholder: true
    });
  }

  // Insert new page data at correct position
  for (let i = 0; i < data.length; i++) {
    updatedItems[startIndex + i] = data[i];
  }

  return updatedItems;
}

/**
 * Updates pagination metadata in state
 *
 * @param state - Current paginated state
 * @param meta - New pagination metadata
 */
export function updatePaginationMeta<T>(
  state: PaginatedState<T>,
  meta: PaginationMeta
): void {
  state.currentPage = meta.page;
  state.pageSize = meta.per_page;
  state.totalItems = meta.total;
  state.totalPages = meta.pages;
  state.hasNext = meta.has_next;
  state.hasPrev = meta.has_prev;
}

/**
 * Generic fulfilled reducer for initial fetch with sparse array
 * Handles both reset (new data) and append (additional data) modes
 *
 * @param state - Current paginated state
 * @param action - Fulfilled action payload
 * @param placeholderPrefix - Prefix for placeholder IDs
 *
 * @example
 * .addCase(fetchTaskItems.fulfilled, (state, action) => {
 *   state.isLoading = false;
 *   handlePaginatedFulfilledWithSparseArray(state, action, 'task-item');
 * })
 */
export function handlePaginatedFulfilledWithSparseArray<T extends { id: string }>(
  state: PaginatedState<T>,
  action: PayloadAction<{
    data: T[];
    meta: PaginationMeta;
    reset: boolean;
    page: number;
    filters?: Record<string, unknown>;
  }>,
  placeholderPrefix: string
): void {
  const { data, meta, reset, page } = action.payload;

  if (reset) {
    // Reset mode: create new sparse array
    state.items = createSparseArray(data, meta, placeholderPrefix) as T[];
    state.loadedPages = new Set([page]);

    // Update filters if provided
    if (action.payload.filters && 'filters' in state) {
      (state as any).filters = action.payload.filters;
    }
  } else {
    // Append mode: add to existing data (remove duplicates)
    const existingIds = new Set(state.items.map((item) => item.id));
    const newItems = data.filter((item) => !existingIds.has(item.id));
    state.items.push(...newItems as T[]);
    state.loadedPages.add(page);
  }

  // Update pagination metadata
  updatePaginationMeta(state, meta);
  state.error = null;
}

/**
 * Generic fulfilled reducer for loading more pages with sparse array
 * Inserts data at correct position in sparse array
 *
 * @param state - Current paginated state
 * @param action - Fulfilled action payload
 * @param placeholderPrefix - Prefix for placeholder IDs
 *
 * @example
 * .addCase(fetchMoreTaskItems.fulfilled, (state, action) => {
 *   state.isLoadingMore = false;
 *   handleLoadMoreFulfilledWithSparseArray(state, action, 'task-item');
 * })
 */
export function handleLoadMoreFulfilledWithSparseArray<T extends { id: string }>(
  state: PaginatedState<T>,
  action: PayloadAction<{
    data: T[];
    meta: PaginationMeta;
    page: number;
  } | null>,
  placeholderPrefix: string
): void {
  if (action.payload) {
    const { data, meta, page } = action.payload;

    // Insert data at correct position in sparse array
    state.items = insertPageData(
      state.items as (T | PlaceholderItem)[],
      data,
      page,
      state.pageSize,
      meta.total,
      placeholderPrefix
    ) as T[];

    state.loadedPages.add(page);

    // Update pagination metadata
    updatePaginationMeta(state, meta);
  }

  state.error = null;
}

/**
 * Creates initial paginated state
 * Use this to initialize slice state
 *
 * @param pageSize - Default page size (default: 100)
 * @returns Initial paginated state object
 *
 * @example
 * const initialState: TaskItemState = {
 *   ...createInitialPaginatedState(100),
 *   filters: {}
 * };
 */
export function createInitialPaginatedState<T>(pageSize = 100): PaginatedState<T> {
  return {
    items: [],
    isLoading: false,
    isLoadingMore: false,
    error: null,
    currentPage: 1,
    pageSize,
    totalItems: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
    loadedPages: new Set()
  };
}

/**
 * Generic reset data reducer
 * Clears all pagination state
 *
 * @param state - Current paginated state
 *
 * @example
 * resetData: (state) => {
 *   resetPaginatedData(state);
 *   state.filters = {};
 * }
 */
export function resetPaginatedData<T>(state: PaginatedState<T>): void {
  state.items = [];
  state.loadedPages = new Set();
  state.currentPage = 1;
  state.totalItems = 0;
  state.totalPages = 0;
  state.hasNext = false;
  state.hasPrev = false;
}
